
/**
 * Deeply compares the objects by recursively comparing all their properties,
 * objects with circular references are not supported, prototype members and functions are ignored
 */
export function deepCompare(objA: {} | null, objB: {} | null, equivalent?: (a: {}, b: {}) => boolean): boolean {
  // keep track of references that have been compared to find circular references while walking down either object
  const refsComparedA: {}[] = [];
  const refsComparedB: {}[] = [];
  const compare = equivalent ? equivalent : (a: {}, b: {}): boolean => { return (a === b); };
  function equals<T>(a: T | null, b: T | null): boolean {
    // try a simple equality test first
    if (a === b) {
      return true;
    }

    if ((a === null) || (b === null)) {
      return false;
    }

    // if both are objects, then further comparison is required
    if ((typeof (a) === 'object') && (typeof (b) === 'object')) {
      // perform deep comparison over object's properties
      const aKeys = <(keyof T)[]>Object.keys(a).sort();
      const bKeys = <(keyof T)[]>Object.keys(b).sort();
      // does one object have a different number of properties?
      if (aKeys.length !== bKeys.length) {
        return false;
      }
      const keysMatch = aKeys.every((key: keyof T, index: number) => {
        // key names should match
        if (key !== bKeys[index]) {
          return false;
        } else if (typeof (a[key]) === 'function' || typeof (b[key]) === 'function') {
          // skip comparison of function properties
          return true;
          // @ts-ignore
        } else if (!compare(a[key], b[key])) {
          // if this is an Object then recursive testing is needed on its properties
          if (typeof a[key] === 'object') {
            // if this property was encountered before then we're going in circles, give up
            // @ts-ignore
            if (refsComparedA.indexOf(a[key]) !== -1) {
              throw new Error(
                `Cannot perform DeepCompare() because a circular reference was encountered, object: ${a}, property: ${key}`
              );
            }
            // @ts-ignore
            refsComparedA.push(a[key]);
            // @ts-ignore
            if (refsComparedB.indexOf(b[key]) !== -1) {
              throw new Error(
                `Cannot perform DeepCompare() because a circular reference was encountered, object: ${b}, property: ${key}`
              );
            }
            // @ts-ignore
            refsComparedB.push(b[key]);

            // recursive compare object's properties
            if (!equals(a[key], b[key])) {
              return false;
            }

            refsComparedA.pop();
            refsComparedB.pop();
            return true;
          } else {
            // properties don't match
            return false;
          }
        } else {
          return true;
        }
      });
      if (!keysMatch) {
        return false;
      }
    } else {
      // parameters aren't equal, and at least one is not an Object
      return false;
    }

    // everything is equal
    return true;
  }

  return equals(objA, objB);
}
